import React, { useState, createContext, useContext, useEffect } from "react";
import { useApiService } from "../shared/apiservice";
import { SyncIssue, User } from "../shared/types";

function parseSyncIssueData(raw: unknown): Record<string, unknown> {
    if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
        return {};
    }
    return { ...(raw as Record<string, unknown>) };
}

function parseUser(d: unknown): User {
    const raw = (d ?? {}) as Record<string, unknown>;
    return {
        id: Number(raw.id ?? 0),
        name: String(raw.name ?? "—"),
        email: String(raw.email ?? ""),
        functions: Array.isArray(raw.functions) ? raw.functions.map(String) : [],
    };
}

function parseSyncIssue(d: Record<string, unknown>): SyncIssue {
    const rawDate = d.date ?? d.created_at;
    return {
        id: Number(d.id),
        date: rawDate ? new Date(String(rawDate)) : new Date(),
        type: String(d.type ?? ""),
        content: String(d.content ?? ""),
        data: parseSyncIssueData(d.data),
        user: parseUser(d.user),
    };
}

const syncIssuesListContext = createContext<{
    syncIssues: SyncIssue[];
    errors: string[];
    loading: boolean;
    page: number;
    perPage: number;
    total: number;
    searchTerm: string;
    fetchSyncIssues: (overridePage?: number) => void;
    setPage: (page: number) => void;
    setPerPage: (perPage: number) => void;
    setSearchTerm: (term: string) => void;
}>({
    syncIssues: [],
    errors: [],
    loading: false,
    page: 1,
    perPage: 15,
    total: 0,
    searchTerm: "",
    fetchSyncIssues: () => { },
    setPage: () => { },
    setPerPage: () => { },
    setSearchTerm: () => { },
});

function useProviderSyncIssues() {
    const [syncIssues, setSyncIssues] = useState<SyncIssue[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPageInternal] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTermInternal] = useState("");
    const apiService = useApiService();

    function setSearchTerm(term: string) {
        setSearchTermInternal(term);
        setPageInternal(1);
    }

    function setPage(p: number) {
        setPageInternal(p);
    }

    useEffect(() => {
        fetchSyncIssues();
    }, [page, perPage, searchTerm]);

    async function fetchSyncIssues(overridePage?: number) {
        const pageToFetch = overridePage ?? page;
        setLoading(true);
        setErrors([]);

        try {
            const params = new URLSearchParams({
                page: String(pageToFetch),
                per_page: String(perPage),
            });
            if (searchTerm.trim()) {
                params.set("q", searchTerm.trim());
            }
            const response = await apiService.get(
                `/sync-issues?${params.toString()}`
            );
            if (!response.ok) {
                setErrors(["Loading sync issues failed. Please try again."]);
                return;
            }
            const json = await response.json();
            const data: SyncIssue[] = (json.data ?? json).map((item: Record<string, unknown>) =>
                parseSyncIssue(item)
            );
            setTotal(json.meta?.total ?? data.length);
            setSyncIssues(data);
        } catch (error) {
            console.error(error);
            setErrors(["Loading sync issues failed. Please try again."]);
        } finally {
            setLoading(false);
        }
    }

    return {
        syncIssues,
        errors,
        loading,
        page,
        perPage,
        total,
        searchTerm,
        fetchSyncIssues,
        setPage,
        setPerPage,
        setSearchTerm,
    };
}

export function ProviderSyncIssues({ children }: { children: React.ReactNode }) {
    const value = useProviderSyncIssues();
    return <syncIssuesListContext.Provider value={value}>{children}</syncIssuesListContext.Provider>;
}

export const useSyncIssues = () => useContext(syncIssuesListContext);

const syncIssueDetailContext = createContext<{
    syncIssue: SyncIssue | null;
    errors: string[];
    loading: boolean;
    deleting: boolean;
    fetchSyncIssue: () => void;
    deleteSyncIssue: () => Promise<boolean>;
}>({
    syncIssue: null,
    errors: [],
    loading: false,
    deleting: false,
    fetchSyncIssue: () => { },
    deleteSyncIssue: async () => false,
});

function useProviderSyncIssueDetail(issueId: number | null | undefined) {
    const [syncIssue, setSyncIssue] = useState<SyncIssue | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const apiService = useApiService();

    async function fetchSyncIssue() {
        if (!issueId) return;
        setLoading(true);
        setErrors([]);

        try {
            const response = await apiService.get(`/sync-issues/${issueId}`);
            if (!response.ok) {
                setErrors(["Loading sync issue failed. Please try again."]);
                return;
            }
            const json = await response.json();
            setSyncIssue(parseSyncIssue(json as Record<string, unknown>));
        } catch (error) {
            console.error(error);
            setErrors(["Loading sync issue failed. Please try again."]);
        } finally {
            setLoading(false);
        }
    }

    async function deleteSyncIssue(): Promise<boolean> {
        if (!issueId) return false;
        setDeleting(true);
        setErrors([]);

        try {
            const response = await apiService.delete(`/sync-issues/${issueId}/delete`);
            if (!response.ok) {
                setErrors(["Deleting sync issue failed. Please try again."]);
                return false;
            }
            return true;
        } catch (error) {
            console.error(error);
            setErrors(["Deleting sync issue failed. Please try again."]);
            return false;
        } finally {
            setDeleting(false);
        }
    }

    useEffect(() => {
        if (issueId) {
            fetchSyncIssue();
        } else {
            setSyncIssue(null);
            setErrors([]);
        }
    }, [issueId]);

    return {
        syncIssue,
        errors,
        loading,
        deleting,
        fetchSyncIssue,
        deleteSyncIssue,
    };
}

export function ProviderSyncIssueDetail({
    children,
    issueId,
}: {
    children: React.ReactNode;
    issueId: number | null | undefined;
}) {
    const value = useProviderSyncIssueDetail(issueId);
    return <syncIssueDetailContext.Provider value={value}>{children}</syncIssueDetailContext.Provider>;
}

export const useSyncIssueDetail = () => useContext(syncIssueDetailContext);
